import { useState, useEffect, useCallback } from 'react'
import { RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { KpiCard } from './analytics/KpiCard'
import { getMarketingAnalytics } from '@/src/lib/api/admin-analytics'
import type { MarketingAnalytics } from '@/src/types/admin/analytics'

const FUNNEL_COLORS = ['#6366f1', '#06b6d4', '#10b981']

export default function AdminMarketingDashboard() {
  const [data, setData] = useState<MarketingAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getMarketingAnalytics()
      setData(result)
      setLastUpdated(new Date())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const avgChurnRate =
    data && data.churn_by_package.length > 0
      ? data.churn_by_package.reduce((sum, r) => sum + r.churn_rate_30d, 0) /
        data.churn_by_package.length
      : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">การตลาดและการเติบโต</h1>
          <p className="text-muted-foreground">การเติบโต, Conversion Funnel และการวิเคราะห์การออกจากระบบ</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              อัพเดตล่าสุด {lastUpdated.toLocaleTimeString('th-TH')}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            รีเฟรช
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <KpiCard
          title="ลูกค้าใหม่เดือนนี้"
          value={data?.acquisition.total_this_month ?? '—'}
          loading={loading && !data}
        />
        <KpiCard
          title="KYC Approval Rate"
          value={
            data?.kyc_funnel
              ? (() => {
                  const submitted = data.kyc_funnel.find((s) => s.stage === 'ส่ง KYC')
                  const approved = data.kyc_funnel.find((s) => s.stage === 'KYC ผ่าน')
                  if (!submitted || submitted.count === 0) return '—'
                  return `${(((approved?.count ?? 0) / submitted.count) * 100).toFixed(1)}%`
                })()
              : '—'
          }
          loading={loading && !data}
        />
        <KpiCard
          title="Churn Rate (30 วัน)"
          value={avgChurnRate !== null ? `${avgChurnRate.toFixed(2)}%` : '—'}
          loading={loading && !data}
          invertTrend
        />
      </div>

      {/* Weekly Acquisition + KYC Funnel */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ลูกค้าใหม่รายสัปดาห์</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !data ? (
              <Skeleton className="h-[260px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={(data?.acquisition.weekly ?? []).map((w) => ({
                    week: w.week_start.slice(5), // MM-DD
                    ลูกค้าใหม่: w.new_customers,
                  }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={32} />
                  <Tooltip />
                  <Bar dataKey="ลูกค้าใหม่" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">KYC Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !data ? (
              <Skeleton className="h-[260px] w-full" />
            ) : (
              <div className="space-y-3 pt-2">
                {(data?.kyc_funnel ?? []).map((stage, idx) => (
                  <div key={stage.stage}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize font-medium">{stage.stage}</span>
                      <span>
                        {stage.count.toLocaleString()} ({stage.pct.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="h-6 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full rounded transition-all"
                        style={{
                          width: `${stage.pct}%`,
                          backgroundColor: FUNNEL_COLORS[idx % FUNNEL_COLORS.length],
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Churn by Package + Peak Hours */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Churn Rate ตามแพ็กเกจ (30 วัน)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !data ? (
              <Skeleton className="h-[260px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  layout="vertical"
                  data={(data?.churn_by_package ?? []).map((r) => ({
                    package: r.package_code,
                    'Churn %': parseFloat(r.churn_rate_30d.toFixed(2)),
                  }))}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey="package"
                    type="category"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={80}
                  />
                  <Tooltip />
                  <Bar dataKey="Churn %" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Peak Hours (ชั่วโมงที่ใช้งานมาก)</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !data ? (
              <Skeleton className="h-[260px] w-full" />
            ) : !data?.peak_hours?.length ? (
              <div className="flex items-center justify-center h-[260px] text-sm text-muted-foreground">
                ไม่มีข้อมูลการใช้งาน 7 วันที่ผ่านมา
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={(data.peak_hours).map((p) => ({
                    hour: `${p.hour}:00`,
                    requests: p.request_count,
                  }))}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={32} />
                  <Tooltip />
                  <Bar dataKey="requests" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Requests" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
